import { injectable } from 'inversify';

import Analytics from './expo-analytics';

import container from '~ioc/ioc';
import type {
  AnalyticsAbandonCheckoutEvent,
  AnalyticsBeginCheckoutEvent,
  AnalyticsEvent,
  AnalyticsLoginEvent,
  AnalyticsPurchaseEvent,
  AnalyticsReferralEvent,
  AnalyticsShareEvent,
  AnalyticsSignUpEvent,
  AnalyticsWishlistEvent,
  EventLogParameters,
} from '~types/analytics';
import { FirebaseLegacyAnalyticsEvents } from '~types/analytics';

@injectable()
export class FirebaseService {
  public abandonCheckout = async ({
    user,
    context,
    method,
  }: AnalyticsAbandonCheckoutEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.abandonCheckout, {
      OUID: user,
      CONTEXT: context,
      METHOD: method,
    });
  };

  public signUp = async ({ method, user }: AnalyticsSignUpEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.signUp, {
      method,
      OUID: user,
    });
  };

  public login = async ({ method, user }: AnalyticsLoginEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.login, {
      method,
      OUID: user,
    });
  };

  public beginCheckout = async ({
    user,
    productBrand,
    productCategory,
    productId,
    productName,
    productPrice,
    productVariant,
    quantity,
    totalOrderValueWithoutPostage,
  }: AnalyticsBeginCheckoutEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.beginCheckout, {
      OUID: user,
      CURRENCY: 'GBP',
      VALUE: totalOrderValueWithoutPostage,
      ITEMS: JSON.stringify([
        {
          ITEM_ID: productId,
          ITEM_NAME: productName,
          ITEM_VARIANT: productVariant,
          ITEM_BRAND: productBrand,
          ITEM_CATEGORY: productCategory,
          PRICE: productPrice,
          CURRENCY: 'GBP',
          QUANTITY: quantity,
        },
      ]),
    });
  };

  // noinspection JSUnusedGlobalSymbols
  public referral = async ({
    user,
    currency,
    productId,
    productName,
    productPrice,
    productBrand,
    quantity,
  }: AnalyticsReferralEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.referral, {
      OUID: user,
      CURRENCY: currency,
      VALUE: productPrice,
      ITEMS: JSON.stringify([
        {
          ITEM_ID: productId,
          ITEM_NAME: productName,
          ITEM_BRAND: productBrand,
          PRICE: productPrice,
          CURRENCY: currency,
          QUANTITY: quantity,
        },
      ]),
    });
  };

  public purchase = async ({
    user,
    shipping,
    transactionId,
    value,
    productId,
    productName,
    productOption = null,
    productBrand,
    productPrice,
    productCategory = null,
    currency = 'GBP',
    quantity = 1,
  }: AnalyticsPurchaseEvent) => {
    await Analytics.logEvent('purchase', {
      OUID: user,
      CURRENCY: currency,
      SHIPPING: shipping,
      TRANSACTION_ID: transactionId,
      VALUE: value,
      ITEMS: JSON.stringify([
        {
          ITEM_ID: productId,
          ITEM_NAME: productName,
          ITEM_VARIANT: productOption,
          ITEM_BRAND: productBrand,
          ITEM_CATEGORY: productCategory,
          PRICE: productPrice,
          CURRENCY: currency,
          QUANTITY: quantity,
        },
      ]),
    });
  };

  // may be needed it in the future thus not deleted
  // noinspection JSUnusedGlobalSymbols
  public wishlist = async ({
    user,
    currency = 'GBP',
    productId,
    productName,
    productBrand,
    productPrice,
    quantity = 1,
  }: AnalyticsWishlistEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.wishlist, {
      OUID: user,
      CURRENCY: 'GBP',
      VALUE: productPrice,
      ITEMS: JSON.stringify([
        {
          ITEM_ID: productId,
          ITEM_NAME: productName,
          ITEM_BRAND: productBrand,
          PRICE: productPrice,
          CURRENCY: currency,
          QUANTITY: quantity,
        },
      ]),
    });
  };

  // may be needed it in the future
  // noinspection JSUnusedGlobalSymbols
  public share = async ({
    user,
    type,
    method,
    productId,
    productName,
    productBrand,
    productPrice,
    productCategory = null,
    currency = 'GBP',
    quantity = 1,
  }: AnalyticsShareEvent) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.share, {
      OUID: user,
      METHOD: method,
      CONTENT_TYPE: type,
      ITEM_ID: productId,
      ITEMS: JSON.stringify([
        {
          ITEM_NAME: productName,
          ITEM_BRAND: productBrand,
          PRICE: productPrice,
          ITEM_CATEGORY: productCategory,
          CURRENCY: currency,
          QUANTITY: quantity,
        },
      ]),
    });
  };

  // generic info
  public info = async ({ user, action }: { user?: number; action: string }) => {
    await Analytics.logEvent(FirebaseLegacyAnalyticsEvents.info, {
      OUID: user,
      action,
    });
  };

  logEvent = <T extends AnalyticsEvent>({
    event,
    data,
  }: EventLogParameters<T>) => Analytics.logEvent(event, data);
}

const singleton = container.get(FirebaseService);

export default singleton;
